import * as THREE from 'three';

/**
 * Aerial perspective, installed once into the shader library.
 *
 * Stock fog is the last line of every fragment shader: a linear mix toward
 * one colour, and nothing else. A mountain three kilometres off therefore
 * kept every count of its contrast and saturation until the mix ate it whole
 * — which is exactly the "nearby geometry scaled down" read that makes a big
 * landscape look like a diorama.
 *
 * Real air does two things to a distant surface before it swallows it: it
 * loses the surface's own reflected light (saturation and contrast fall), and
 * it scatters sky light into the path (the darks lift toward the haze colour,
 * which `paintSky` keeps tuned to the horizon hour by hour). Both of those
 * were already here.
 *
 * ## What was still wrong, and it was the shape of the model
 *
 * The old ramps were driven by `vFogDepth` alone, so haze was a pure function
 * of DISTANCE. Air is not: it is a fluid in a gravity well, half its mass sits
 * in the bottom few kilometres, and the aerosol that actually does the hazing
 * is packed lower still. Distance-only fog says the sight line from an eye at
 * 550 m to a cloud at 1,000 m passes through exactly as much haze as the same
 * length of sight line along a valley floor. It does not, and getting that
 * wrong cost this world two separate things that both read as "flat":
 *
 *   - **Distance did not lighten.** Measured on the midday gate frame, 300 m
 *     over a camp: mean land luminance was **107 at three kilometres and 106
 *     at two hundred metres**, against a sky of 172. The single strongest
 *     depth cue a landscape has — the far hills going pale — was worth one
 *     luminance level across the whole visible world.
 *   - **The clouds were fogged out of the sky.** With `fogFar` at ~12 km and
 *     the cloud layer living in a 12 km box, a cumulus 9.5 km away sat at a
 *     fog factor of **0.85** — blended 85% into the colour of the sky it was
 *     supposed to be seen against. Cloud base is where the air gets thin and
 *     clear, and the model had it in the thick of the murk.
 *
 * ## The model
 *
 * Density falls off exponentially with height, `rho(y) = rho0 * exp(-y/Hs)`,
 * and the optical depth along a sight line has a closed form, so this costs
 * two `exp` calls and no marching:
 *
 *     tau = rho0 * d * Hs * (exp(-yc/Hs) - exp(-yf/Hs)) / (yf - yc)
 *
 * with the removable singularity at `yf == yc` falling back to the level-ray
 * case `rho0 * d * exp(-yc/Hs)`. Transmittance is `exp(-tau)`, so the fog
 * factor is `1 - exp(-tau)` — a real extinction curve rather than a
 * `smoothstep`, which means it never fully saturates and the far field keeps
 * a little contrast instead of flattening to a wall of haze.
 *
 * `fogFar` still calibrates `rho0`, so a level sea-level sight line of
 * `fogFar` is ~95% extinct and the control keeps meaning what it meant. That
 * matters because the frame loop drives it off altitude and weather, and the
 * render panel's `fogMul` scales it — none of which this may break.
 *
 * **`fogNear` is deliberately NOT a clear zone any more, and that was the
 * measurement that mattered most.** A `smoothstep` starts at zero and so
 * needs a near plane to start from; an exponential does not, because it
 * already delivers almost nothing over a short ray. Keeping the subtraction
 * meant the frame loop's `alt * 3.2 + 300` was carving out a hole of
 * perfectly clear air **1,260 m across at the 300 m gate altitude, and 9,900
 * m at three kilometres** — which is most of, and at altitude all of, what
 * is actually on screen. With it in place the far-to-near luminance spread
 * over the gate frame was 4.0 levels; the whole point of the exercise is
 * that it should be tens. `fogNear` is now left to the one job the frame
 * loop still uses it for — being pulled in by rain and fog weather — via
 * `fogFar`, which those same lines already move.
 *
 * ## Two implementation notes worth keeping
 *
 * **The shared uniforms are plain objects, and that is the whole trick.** A
 * custom uniform has to reach every fogged material in the scene, and three
 * clones a material's uniforms at first compile — `UniformsUtils.clone` calls
 * `.clone()` on anything that is a Vector, Color or Matrix, and `.slice()` on
 * an array — so a `Vector3` value would stop being shared the moment the
 * second material compiled. A **plain `{ x, y, z, w }` object** is none of
 * those, is copied by reference, and three's `setValueV4f` uploads it by its
 * `.x .y .z .w` fields exactly as it would a Vector4. So one object, written
 * once a frame by `setAerialSky`, is what every material's `fogSun` /
 * `fogSkyMid` / `fogSkyGlow` uniform points at. They are added to every entry
 * of `THREE.ShaderLib` here (`installAerialPerspective` runs before the
 * renderer exists, so before any material compiles) and declared in
 * `fog_pars_fragment` below; a `ShaderMaterial` with `fog: true` that does
 * not carry them (the clouds, the fire) simply has them unbound — three's
 * `seqWithValue` skips a uniform the material has no value for — and its haze
 * falls back to the flat `fogColor` mix, which is what it had.
 *
 * (Until 2026-08-18 this file said "no new uniform, anywhere" and derived
 * everything from `mvPosition`, `viewMatrix` and `cameraPosition`. That was
 * enough for HEIGHT; it is not enough for a haze that is brighter toward the
 * sun than away from it, which needs to know where the sun is.)
 *
 * **The world position is derived from `mvPosition`, not from `transformed`.**
 * The obvious `modelMatrix * vec4(transformed, 1.0)` is wrong for every
 * InstancedMesh in this world — vegetation, rocks, structures, figures all
 * carry their placement in `instanceMatrix`, which `modelMatrix` knows
 * nothing about — and replicating the instancing branch would then bind this
 * chunk to the exact shape of three's `project_vertex`. Rotating the
 * view-space position back to world through the transpose of `viewMatrix`
 * needs none of that and is correct for instanced, skinned and morphed
 * geometry alike, because by then all of it is already in `mvPosition`.
 *
 * **And the transpose was wrong until 2026-08-18.** `world = eye + Rᵀ·view`,
 * and in GLSL `(Rᵀ v).y = dot(viewMatrix[1].xyz, v)` — column 1 of the view
 * matrix. The old line took `(viewMatrix[0][1], viewMatrix[1][1],
 * viewMatrix[2][1])`, which is ROW 1, i.e. `(R v).y`: the view-space vector
 * rotated by the camera a second time. Checked numerically in the running
 * game: a point 45.6 m below the eye came out 7.1 m ABOVE it. So the height
 * fog had been fed a height that swung with the camera's pitch and yaw
 * rather than the land's, and everything tuned against it — the scale height,
 * `TAU_AT_FAR` — was tuned against a moving target. The constants below were
 * re-judged at the capture stations after the fix.
 */

/**
 * Aerosol scale height in metres — the altitude by which haze thins to 1/e.
 *
 * The real boundary layer over a temperate ocean sits around 1 km and the
 * aerosol that hazes a view is mostly inside it; free troposphere above that
 * is comparatively clear. 900 m puts the island's 2,560 m peaks a little over
 * two scale heights up, so summits stand clear of the murk their own valleys
 * are full of, which is the behaviour this exists to buy.
 */
const SCALE_HEIGHT = 900;

/**
 * Optical depth at `fogFar` along a level sea-level ray. 3.0 is 95% extinct —
 * so `fogFar` still means "about as far as you can see", as it did when it
 * was the top of a `smoothstep`.
 */
const TAU_AT_FAR = 3.0;

/** How much of the extinction also shows up as loss of saturation. */
const DESATURATE = 0.55;

/**
 * The sky the haze dissolves into — the SAME numbers `paintSky` hands the
 * dome, so far land fades to exactly the sky standing behind it. Plain
 * objects (see the header): `fogSun` is the sun's world direction with the
 * dome's exposure in `w`; `fogSkyMid` and `fogSkyGlow` are the dome's horizon
 * band and sun glow, LINEAR, before the dome's own ACES — the fragment
 * chunk applies that curve and the gamma itself, so the value it mixes toward
 * is display space, which is the space three applies fog in.
 */
const fogSun = { x: 0, y: 1, z: 0, w: 0.92 };
const fogSkyMid = { x: 0.4, y: 0.4, z: 0.45 };
const fogSkyGlow = { x: 1, y: 0.9, z: 0.8 };
const AERIAL_UNIFORMS = { fogSun, fogSkyMid, fogSkyGlow };

/**
 * Called once a frame from wherever `paintSky` is called, with the sky's own
 * state: the sun's world direction, the dome's `mid` and `glow` bands (linear,
 * 0..1) and its exposure. Writes into the shared objects; nothing else needs
 * to know.
 */
export function setAerialSky(
  sunDir: { x: number; y: number; z: number },
  mid: { r: number; g: number; b: number },
  glow: { r: number; g: number; b: number },
  expo: number,
): void {
  fogSun.x = sunDir.x; fogSun.y = sunDir.y; fogSun.z = sunDir.z; fogSun.w = expo;
  fogSkyMid.x = mid.r; fogSkyMid.y = mid.g; fogSkyMid.z = mid.b;
  fogSkyGlow.x = glow.r; fogSkyGlow.y = glow.g; fogSkyGlow.z = glow.b;
}

export function installAerialPerspective(): void {
  // every built-in material gets the shared sky uniforms; see the header for
  // why plain objects, and why this must run before the first compile
  for (const lib of Object.values(THREE.ShaderLib)) {
    if (!lib || !lib.uniforms) continue;
    for (const [k, v] of Object.entries(AERIAL_UNIFORMS)) lib.uniforms[k] = { value: v };
  }
  THREE.ShaderChunk.fog_pars_vertex = /* glsl */ `
#ifdef USE_FOG
	varying float vFogDepth;
	varying vec3 vFogW;
#endif
`;

  THREE.ShaderChunk.fog_vertex = /* glsl */ `
#ifdef USE_FOG
	vFogDepth = - mvPosition.z;
	// world position, rotated back out of view space: world = Rᵀ·view + eye,
	// and (Rᵀ v)_i = dot( viewMatrix[ i ].xyz, v ) — the COLUMNS of the view
	// matrix (see the header for the row/column bug this replaces). Correct
	// for instanced/skinned/morphed geometry alike, because all of it is
	// already baked into mvPosition.
	vFogW = cameraPosition + vec3(
		dot( viewMatrix[ 0 ].xyz, mvPosition.xyz ),
		dot( viewMatrix[ 1 ].xyz, mvPosition.xyz ),
		dot( viewMatrix[ 2 ].xyz, mvPosition.xyz ) );
#endif
`;

  THREE.ShaderChunk.fog_pars_fragment = /* glsl */ `
#ifdef USE_FOG
	uniform vec3 fogColor;
	// the sky the haze is made of — shared plain-object uniforms, see aerial.ts
	uniform vec4 fogSun;
	uniform vec3 fogSkyMid;
	uniform vec3 fogSkyGlow;
	varying float vFogDepth;
	varying vec3 vFogW;
	#ifdef FOG_EXP2
		uniform float fogDensity;
	#else
		uniform float fogNear;
		uniform float fogFar;
	#endif
	// the dome's own tone curve, so the haze lands on the sky's display value
	vec3 fogAces( vec3 x ) {
		return clamp( ( x * ( 2.51 * x + 0.03 ) ) / ( x * ( 2.43 * x + 0.59 ) + 0.14 ), 0.0, 1.0 );
	}
#endif
`;

  THREE.ShaderChunk.fog_fragment = /* glsl */ `
#ifdef USE_FOG
	#ifdef FOG_EXP2
		float fogFactor = 1.0 - exp( - fogDensity * fogDensity * vFogDepth * vFogDepth );
	#else
		// no clear zone: an exponential already gives almost nothing over a
		// short ray, and subtracting fogNear carved a kilometre of perfectly
		// clear air out of the middle of every frame
		float fogDist = vFogDepth;
		// rho0 is calibrated so a level sea-level ray of fogFar is ~95% out
		float fogRho = ${TAU_AT_FAR.toFixed(1)} / max( 1.0, fogFar );

		float fogYc = max( 0.0, cameraPosition.y );
		float fogYf = max( 0.0, vFogW.y );
		float fogEc = exp( - fogYc / ${SCALE_HEIGHT.toFixed(1)} );
		float fogEf = exp( - fogYf / ${SCALE_HEIGHT.toFixed(1)} );
		float fogDy = fogYf - fogYc;
		// mean density along the ray — the closed form, with its removable
		// singularity at a level ray handled rather than divided by
		float fogMean = abs( fogDy ) < 1.0
			? fogEc
			: ( fogEc - fogEf ) * ${SCALE_HEIGHT.toFixed(1)} / fogDy;

		float fogTau = fogRho * fogDist * fogMean;
		// extinction, not a smoothstep: this never quite reaches 1, so the far
		// field keeps a little contrast instead of flattening into a wall
		float fogFactor = 1.0 - exp( - fogTau );
	#endif
	{
		// the air takes a surface's saturation first, and only then the rest
		float hazeLum = dot( gl_FragColor.rgb, vec3( 0.299, 0.587, 0.114 ) );
		gl_FragColor.rgb = mix( gl_FragColor.rgb, vec3( hazeLum ),
			fogFactor * ${DESATURATE.toFixed(2)} );
	}
	// THE HAZE IS THE SKY (2026-08-18). Three applies fog after tone mapping,
	// in display space; paintSky used to hand it the dome's LINEAR mid band
	// times 0.92, so far land dissolved into a haze about half as bright as
	// the sky standing behind it — a dark wall under a bright horizon, which
	// is why distance never lifted. Now the haze is the dome's own colour
	// along this sight line — mid band plus the sun's glow, through the dome's
	// own ACES and gamma — so a ridge fades into precisely the sky above it,
	// warm and bright toward the sun, cool and dim away from it. A material
	// that carries no fogSun (a ShaderMaterial with fog: true, e.g. clouds)
	// gets fogSun.w = 0 and falls back to the flat fogColor it always had.
	vec3 fogHaze = fogColor;
	if ( fogSun.w > 0.001 ) {
		vec3 fogView = normalize( vFogW - cameraPosition );
		float fogD = max( dot( fogView, fogSun.xyz ), 0.0 );
		// the dome's tight lobe, and a wider one than the dome draws: haze is
		// forward-scattering air, warmer over a broader arc than the sky behind
		vec3 fogC = fogSkyMid + fogSkyGlow * ( pow( fogD, 7.0 ) * 0.18 + pow( fogD, 2.0 ) * 0.12 );
		fogHaze = pow( fogAces( fogC * fogSun.w ), vec3( 1.0 / 2.2 ) );
	}
	gl_FragColor.rgb = mix( gl_FragColor.rgb, fogHaze, fogFactor );
#endif
`;
}
